import React, { createContext, useState, useContext, useCallback } from "react";
import axiosInstance from "../../utility/axiosInstance";
const apiContext = createContext();

export const ApiProvider = ({ children }) => {
  const [specialityToken, setSpecialityToken] = useState(null);
  const [specialityList, setSpecialityList] = useState([]);
  const [doctorList, setDoctorList] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [loadingDoctor, setLoadingDoctor] = useState(false);
  const [isLoadingSpecialties, setIsLoadingSpecialties] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const isDev = process.env.REACT_APP_PHASE === "development";

  const addNotification = (message, type = "error") => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
  };

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const retryRequest = async (fn, retries = 3, delay = 500) => {
    try {
      return await fn();
    } catch (error) {
      if (retries > 0 && error?.response?.status === 500) {
        console.warn(`Retrying... attempts left: ${retries}`);
        await new Promise((res) => setTimeout(res, delay));
        return retryRequest(fn, retries - 1, delay * 2);
      }
      // Add a flag so we know retries are exhausted
      error.retriesExhausted = true;
      throw error;
    }
  };
  const fetchTokenAndSpecialityList = useCallback(async () => {
    setIsLoadingSpecialties(true);
    try {
      const tokenResponse = await retryRequest(() =>
        axiosInstance.post("/v1/webhook/users/d2YzMzIw/generate-token", {
          user_name: "Asianheart-website",
          user_password: "Asian",
        }),
      ).catch((error) => {
        if (error.retriesExhausted) {
          alert(
            "Service is temporarily unavailable. Please try again later or refresh the page.",
          );
          return null;
        }
        throw error;
      });

      if (!tokenResponse) return;

      const { data: tokenData } = tokenResponse;
      setSpecialityToken(tokenData);

      const { request_id, access_token } = tokenData || {};

      if (request_id && access_token) {
        await retryRequest(() =>
          axiosInstance.post(
            "/v1/webhook/users/d2YzMzIw/get-patient-details",
            { request_id, Regno: "Loc00001" },
            { headers: { Authorization: `Bearer ${access_token}` } },
          ),
        )
          .then((listResponse) => {
            setSpecialityList(listResponse.data);
          })
          .catch((error) => {
            // Only alert if all 3 retries are exhausted
            if (error.retriesExhausted) {
              alert(
                "Unable to load specialities. Please try again later or refresh the page.",
              );
            } else {
              throw error;
            }
          });
      } else {
        console.warn("Token generation returned incomplete data:", tokenData);
      }
    } catch (error) {
      console.error("Unexpected error:", error?.message || error);
    } finally {
      setIsLoadingSpecialties(false); 
    }
  }, [setSpecialityToken, setSpecialityList]);

  const fetchTokenAndSpecialityDoctorList = async (selectedSpecialty) => {
    try {
      setLoadingDoctor(true);

      const response = await axiosInstance.post(
        isDev
          ? "/TApiQuery?webformid=wf3384&terminalid=114.143.230.211"
          : "/TApiQuery?webformid=wf3407&terminalid=172.16.17.6",
        new URLSearchParams({
          data: JSON.stringify([
            {
              REGNO: selectedSpecialty?.value,
            },
          ]),
        }),
        {
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      );

      setDoctorList(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching doctor list:", error);
    } finally {
      setLoadingDoctor(false);
    }
  };

  const fetchDoctorSlots = async (doctorId, fromDate, toDate) => {
    try {
      const response = await axiosInstance.post(
        isDev
          ? "/TApiQuery?webformid=wf3359&terminalid=114.143.230.211"
          : `/TApiQuery?webformid=wf3355&terminalid=${process.env.REACT_APP_TERMINAL_ID}`,
        {
          data: JSON.stringify([
            {
              DOCTORID: doctorId,
              FROMDTS: fromDate,
              TODATS: toDate,
            },
          ]),
        },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching slots:", error);
      throw error;
    }
  };

  return (
    <apiContext.Provider
      value={{
        specialityList,
        fetchTokenAndSpecialityList,
        doctorList,
        specialityToken,
        setSelectedSpecialty,
        selectedSpecialty,
        fetchTokenAndSpecialityDoctorList,
        setSelectedDoctor,
        selectedDoctor,
        loadingDoctor,
        addNotification,
        removeNotification,
        notifications,
        fetchDoctorSlots,
        setDoctorList,
        setSpecialityList,
        isLoadingSpecialties,
      }}
    >
      {children}
    </apiContext.Provider>
  );
};

export const useApi = () => useContext(apiContext);
